import {
  useEffect,
  useState,
  useRef,
  type MutableRefObject,
  type ChangeEvent,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { IoMdArrowBack } from 'react-icons/io';
import { IoIosSearch } from 'react-icons/io';
import { IoLocationOutline } from 'react-icons/io5';
import { BsPerson } from 'react-icons/bs';

import { fetchTenants } from '../../redux/tenantActions';
import { fetchProgramWithId } from '../../redux/programActions';

type RootState = any;

function TenantPage() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tenant, loading } = useSelector((state: RootState) => state.tenant);
  const { program } = useSelector((state: RootState) => state.program);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchVisible, setSearchVisible] = useState(false);
  const [filteredTenants, setFilteredTenants] = useState<any[]>([]);
  const searchRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!id) return;
    // @ts-expect-error thunk
    dispatch(fetchTenants(id));
  }, [dispatch, id, program]);

  useEffect(() => {
    if (!(program && program.programId) && id) {
      // @ts-expect-error thunk
      dispatch(fetchProgramWithId(id));
    }
  }, [dispatch, id, program]);

  useEffect(() => {
    if (tenant?.tenants) {
      const filtered = tenant.tenants.filter((item: any) => {
        if (!item) return false;
        return (
          (item.storeCoordinator?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
          (item.phone?.toString() || '').includes(searchTerm) ||
          (item.tenantId?.toString() || '').includes(searchTerm)
        );
      });
      setFilteredTenants(filtered);
    } else {
      setFilteredTenants([]);
    }
  }, [searchTerm, tenant?.tenants]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node) &&
        !target?.closest('button[aria-label="Toggle search"]')
      ) {
        setSearchVisible(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClick = async (tenantId: number) => {
    try {
      navigate(`/tenant/${tenantId}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const toggleSearchVisibility = () => {
    setSearchVisible((prev) => !prev);
    if (isSearchVisible) {
      setSearchTerm('');
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    if (program?.principalId) {
      navigate(`/principal/${program.principalId}`, { replace: true });
      return;
    }
    navigate('/principal', { replace: true });
  };

  return (
    <div className="h-screen bg-white overflow-auto">
      {loading && (
        <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {tenant && tenant.tenants && (
        <div className="h-full flex flex-col">
          <div className="bg-white border-b shadow-sm sticky top-0 z-30">
            <div className="max-w-[1200px] mx-auto">
              <div className="px-4 sm:px-6">
                <div className="flex items-center h-16">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <IoMdArrowBack className="w-5 h-5 text-gray-600" />
                  </button>
                  <div className="flex-1 flex justify-center">
                    {!isSearchVisible && (
                      <div className="text-center">
                        <h1 className="text-lg font-semibold text-gray-900">Outlets</h1>
                        <div className="text-sm text-gray-500">
                          {program ? program.programName : 'Loading...'}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      ref={searchRef as MutableRefObject<HTMLDivElement | null>}
                      className={`overflow-hidden transition-all duration-300 ease-in-out flex items-center ${
                        isSearchVisible ? 'flex-1 max-w-xs' : 'w-0'
                      }`}
                    >
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search outlets..."
                        disabled={!isSearchVisible}
                        autoFocus={isSearchVisible}
                        className={`w-full h-10 px-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all duration-300 bg-gray-50 ${
                          isSearchVisible
                            ? 'opacity-100 translate-x-0 pointer-events-auto'
                            : 'opacity-0 -translate-x-full pointer-events-none'
                        }`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={toggleSearchVisibility}
                      aria-label="Toggle search"
                      className="flex items-center justify-center w-10 h-10 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors"
                    >
                      <IoIosSearch className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 bg-gray-50/80">
            <div className="p-4 sm:p-6">
              <div className="max-w-5xl mx-auto space-y-4">
                {filteredTenants && filteredTenants.length > 0 ? (
                  filteredTenants.map((item, index) => (
                    <div
                      key={item.tenantId || index}
                      onClick={() => handleClick(item.tenantId)}
                      className="bg-white rounded-xl overflow-hidden shadow-soft cursor-pointer transition-all hover:shadow-card-hover border border-gray-100 active:scale-[0.995]"
                    >
                      <div className="p-5 sm:p-6">
                        <div className="flex items-center gap-4 mb-6">
                          {item.storeLogo && (
                            <img
                              src={`${item.storeLogo}?t=${new Date().getTime()}`}
                              alt="Store Logo"
                              className="w-12 h-12 object-contain bg-gray-50 rounded-lg p-1"
                            />
                          )}
                          <div>
                            <h3 className="font-medium text-gray-900">{item.tenantName}</h3>
                            <p className="text-sm text-gray-500">{item.tenantBrand}</p>
                          </div>
                        </div>

                        <div className="space-y-4 text-sm">
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-start gap-3 text-gray-600">
                              <div className="flex-shrink-0">
                                <IoLocationOutline className="w-5 h-5" />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900 mb-2">Store Location</p>
                                <p>
                                  {item.wing}, {item.floor}
                                </p>
                                <p>{item.address}</p>
                                <p>
                                  {item.city}, {item.state}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-start gap-3 text-gray-600">
                              <div className="flex-shrink-0">
                                <BsPerson className="w-5 h-5" />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900 mb-2">Store Coordinator</p>
                                <p>{item.storeCoordinator}</p>
                                <p>{item.phone}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <IoLocationOutline className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-600 font-medium">No outlets found</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {searchTerm ? 'Try a different search term' : 'No shops in this program'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TenantPage;

